import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppValidators } from 'src/app/common/appValidators';
import { SendEmailApiModel } from 'src/app/models/api/actions/sendEmailApi.model';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { EmailPlaceholder } from 'src/app/services/actions/common/common-actions.service';
import { ActionsHttpServiceService } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';

export interface EmailData {
  to: string;
  subject: string;
  body: string;
  cc?: string;
  integrationId: string;
  violationId?: string;
  resourceId: string;
  resourceMakerEmails: Set<string>;
  emailPlaceholders: EmailPlaceholder[];
}

@Component({
  selector: 'app-action-send-email',
  templateUrl: './action-send-email.component.html',
  styleUrls: ['./action-send-email.component.scss'],
})
export class ActionSendEmailComponent {
  public fg: UntypedFormGroup;
  public waitingForResponse = false;
  public contactMakerTooltip: string;
  public contactMakerChecked = false;
  public supportedEmailPlaceholders: EmailPlaceholder[] = [];
  private readonly _integrationId: string;
  private readonly _maxAmountOfEmails: number = 5;

  constructor(
    public dialogRef: MatDialogRef<ActionSendEmailComponent>,
    @Inject(MAT_DIALOG_DATA) public emailData: EmailData,
    private readonly _actionsHttpService: ActionsHttpServiceService,
    private readonly _fb: UntypedFormBuilder,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    private readonly _labelProvider: LabelsProviderPipe
  ) {
    this._integrationId = emailData.integrationId;
    this.fg = this._fb.group({
      to: this._fb.control(emailData.to, [
        Validators.required,
        AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails),
      ]),
      cc: this._fb.control(emailData.cc || '', [AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails)]),
      subject: this._fb.control(emailData.subject, [Validators.required]),
      body: this._fb.control(emailData.body, [Validators.required]),
    });
    this.contactMakerTooltip =
      this.emailData.resourceMakerEmails.size === 0
        ? (this.contactMakerTooltip = this._labelProvider.transform(
            'commonActions.sendEmailAction.contactMakerDisabledTooltip'
          ))
        : '';
    this.supportedEmailPlaceholders = this.emailData.emailPlaceholders;
  }

  public sendEmail() {
    if (!this.waitingForResponse) {
      this.waitingForResponse = true;
      const ccValue = this.fg.controls['cc'].value?.trim();
      const emailData: SendEmailApiModel = {
        recipients: this.fg.controls['to'].value.split(/[,;]+/).map((email: string) => email.trim()),
        subject: this.fg.controls['subject'].value,
        bodyText: this.fg.controls['body'].value,
        inventory_resource_id: this.emailData.resourceId,
        violation_id: this.emailData.violationId,
      };

      // Only add ccRecipients if CC field has value
      if (ccValue && ccValue.length > 0) {
        emailData.ccRecipients = ccValue.split(/[,;]+/).map((email: string) => email.trim());
      }

      this._actionsHttpService.sendEmail(this._integrationId, emailData).subscribe({
        next: () => {
          this._notificationService.success('Email sent successfully');
          this.dialogRef.close(true);
        },
        error: (httpError: HttpErrorResponse) => {
          if (this._httpErrorHandlerService.isUnhandledExceptionError(httpError.status)) {
            this._httpErrorHandlerService.handle(httpError);
          } else {
            this._notificationService.error(httpError.error.detail);
          }
          this.waitingForResponse = false;
        },
      });
    }
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onContactMakerChange(): void {
    this.contactMakerChecked = !this.contactMakerChecked;
    const toField = this.fg.controls['to'];
    const currentEmails = this._parseEmails(toField.value);
    let updatedEmails: string[];
    if (this.contactMakerChecked) {
      updatedEmails = Array.from(new Set([...currentEmails, ...this.emailData.resourceMakerEmails]));
    } else {
      updatedEmails = currentEmails.filter((email: string) => !this.emailData.resourceMakerEmails.has(email));
    }
    toField.setValue(updatedEmails.join(','));
  }

  private _parseEmails(emails: string): string[] {
    return emails
      .split(/[,;]+/)
      .map((email: string) => email.trim())
      .filter((email: string) => email);
  }
}
